import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Badge,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  AddShoppingCart,
  Favorite,
  FavoriteBorder,
  Share as ShareIcon
} from '@mui/icons-material';
import { ProductFilter, ProductGrid } from '../../components/Products';
import { fetchProducts } from '../../store/slices/productsSlice';
import { addToCart } from '../../store/slices/cartSlice';
import { loadUserFavorites, toggleFavorite, selectIsProductFavorite } from '../../store/slices/favoritesSlice';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import { surface, brand, ink, line, status } from '../../theme/tokens';

// Import product banner images
import productBanner from '../../assets/product_image_1.png';
import productImage2 from '../../assets/product_image_2.png';
import productImage3 from '../../assets/product_image_3.png';
import Seo from '../../components/Common/Seo';

const ProductsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Fallback mobile detection for production builds
  const isActuallyMobile = isMobile || (typeof window !== 'undefined' && window.innerWidth < 768);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Removed complex favorites tracking - ProductCard handles its own state
  const { isAuthenticated } = useSelector(state => state.auth);
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    categories: [],
    farmingMethods: [],
    priceRange: [0, 1000],
    ratings: [],
    inStock: false,
    featured: false,
    organic: false
  });

  // Available filter options
  const [categoriesData, setCategoriesData] = useState([]);
  const farmingMethods = ['Organic', 'Conventional', 'Hydroponic', 'Natural', 'Biodynamic'];
  const maxPrice = Math.max(...(products?.map(p => p.price) || [1000]));

  useEffect(() => {
    loadProducts();
    loadCategories();
    // Load user favorites if authenticated
    if (isAuthenticated) {
      dispatch(loadUserFavorites());
    }
  }, [isAuthenticated, dispatch]);

  // Refresh products when page comes back into focus
  useEffect(() => {
    const handleFocus = () => {
      loadProducts();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadCategories = async () => {
    try {
      const categories = await CategoryService.getActiveCategories();
      setCategoriesData(categories.map(cat => cat.name));
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to default categories
      setCategoriesData(['FRUITS', 'VEGETABLES', 'GRAINS', 'HERBS', 'DAIRY', 'PROCESSED', 'LEAFY_GREENS', 'CITRUS']);
    }
  };

  const loadProducts = async (forceRefresh = false) => {
    setLoading(true);
    try {
      console.log(`🔄 Loading products (forceRefresh: ${forceRefresh})`);
      const response = await ProductService.fetchProductsWithPrimaryImages({
        isActive: { eq: true }
      }, 100, null, forceRefresh);
      setProducts(response.items || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Failed to load products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term and filters
  const filteredProducts = React.useMemo(() => {
    if (!products) return [];

    return products.filter(product => {
      // Search term filter
      const matchesSearch = !searchTerm || 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());

      // Category filter
      const matchesCategory = filters.categories.length === 0 || 
        filters.categories.includes(product.category?.name) ||
        (typeof product.category === 'string' && filters.categories.includes(product.category));

      // Farming method filter
      const matchesFarmingMethod = filters.farmingMethods.length === 0 || 
        filters.farmingMethods.includes(product.farmingMethod);

      // Price range filter
      const productPrice = product.discountPrice || product.price;
      const matchesPrice = productPrice >= filters.priceRange[0] && 
        productPrice <= filters.priceRange[1];

      // Stock filter
      const matchesStock = !filters.inStock || product.stock > 0;

      // Featured filter
      const matchesFeatured = !filters.featured || product.isFeatured;

      // Organic filter
      const matchesOrganic = !filters.organic || product.farmingMethod === 'Organic';

      // Rating filter (if product has reviews)
      const matchesRating = filters.ratings.length === 0 || 
        (product.reviews && product.reviews.length > 0 && 
         filters.ratings.some(rating => {
           const avgRating = product.reviews.reduce((acc, review) => acc + review.rating, 0) / product.reviews.length;
           return avgRating >= rating;
         }));

      return matchesSearch && matchesCategory && matchesFarmingMethod && 
             matchesPrice && matchesStock && matchesFeatured && 
             matchesOrganic && matchesRating;
    });
  }, [products, searchTerm, filters]);

  const handleAddToCart = (product) => {
    // Pass the full product object, not just selected fields
    dispatch(addToCart({
      product: product,
      quantity: 1
    }));
  };

  const handleToggleFavorite = async (product) => {
    if (!isAuthenticated) {
      // Show login prompt
      alert('Please login to add products to favorites');
      navigate('/login');
      return;
    }

    try {
      console.log('Toggle favorite for:', product.name);
      await dispatch(toggleFavorite(product.id));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleLoginRequired = (action, product) => {
    alert(`Please login to ${action === 'favorite' ? 'add products to favorites' : action}`);
    navigate('/login');
  };

  const handleShare = (product) => {
    // TODO: Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.shortDescription || product.description,
        url: window.location.href + '/' + product.id
      });
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href + '/' + product.id);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/products/${product.id}`);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.farmingMethods.length > 0) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice) count++;
    if (filters.inStock) count++;
    if (filters.featured) count++;
    if (filters.organic) count++;
    return count;
  };

  // Create a placeholder image data URL
  const createPlaceholderImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    
    // Fill background
    ctx.fillStyle = surface.sunken;
    ctx.fillRect(0, 0, 100, 100);
    
    // Draw border
    ctx.strokeStyle = line.default;
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, 100, 100);
    
    // Draw text
    ctx.fillStyle = ink.secondary;
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('No Image', 50, 50);
    
    return canvas.toDataURL();
  };

  // Convert signed URLs to public URLs (same logic as ProductCard)
  const getPublicImageUrl = (url) => {
    if (!url) {
      return createPlaceholderImage();
    }
    
    let urlString;
    if (typeof url === 'string') {
      urlString = url;
    } else if (url instanceof URL) {
      urlString = url.toString();
    } else if (typeof url === 'object' && url.href) {
      urlString = url.href;
    } else {
      return createPlaceholderImage();
    }
    
    // Extract the key from the signed URL
    const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
    const region = 'ap-south-1';
    
    // Check if it's already a signed URL from S3
    if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
      // Extract the file path from the signed URL
      const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
      const filePath = urlParts ? urlParts.split('?')[0] : '';
      
      // Return public URL (since files are in public/ folder)
      return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
    }
    
    // If it's already a direct URL or key, return as is
    return urlString;
  };

  const MobileProductCard = ({ product }) => {
    const images = Array.isArray(product.images) 
      ? product.images 
      : product.images?.items || [];
    
    const primaryImageUrl = images.length > 0 
      ? getPublicImageUrl(images[0]?.url) 
      : getPublicImageUrl(product.primaryImage?.url);
    
    // Check if product is favorited
    const isFavorited = useSelector(selectIsProductFavorite(product.id));

    // Calculate discount percentage
    const discountPercent = product.discountPrice && product.price 
      ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
      : 0;

    // Stock status
    const isLowStock = product.stock > 0 && product.stock <= 5;
    const isOutOfStock = product.stock === 0;

    return (
      <Card
        elevation={0}
        sx={{
          mb: 2,
          cursor: 'pointer',
          position: 'relative',
          backgroundColor: surface.card,
          border: `1px solid ${line.subtle}`,
          borderRadius: 3,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            borderColor: line.default,
            boxShadow: '0 8px 20px rgba(75,58,55,0.10)'
          }
        }}
        onClick={() => handleProductClick(product)}
      >

        <Box sx={{ display: 'flex', p: 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="img"
              sx={{
                width: 90,
                height: 90,
                borderRadius: 1,
                objectFit: 'cover',
                mr: 1.5,
                flexShrink: 0,
                backgroundColor: surface.sunken
              }}
              image={primaryImageUrl}
              alt={product.name}
              onError={(e) => {
                e.target.src = createPlaceholderImage();
              }}
            />
            
            {/* Stock Status Indicator */}
            {isOutOfStock && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 12,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                >
                  OUT OF STOCK
                </Typography>
              </Box>
            )}
          </Box>

          <CardContent sx={{ flex: 1, p: 0, '&:last-child': { pb: 0 } }}>
            {/* Product Name */}
            <Typography 
              variant="h6" 
              component="h3"
              sx={{ 
                fontSize: '1rem',
                mb: 0.5,
                lineHeight: 1.2,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                color: ink.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {product.name}
            </Typography>

            {/* Badges Row */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
              {/* Discount Badge */}
              {discountPercent > 0 && (
                <Chip
                  label={`${discountPercent}% OFF`}
                  color="error"
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}

              {/* Featured Badge */}
              {product.isFeatured && (
                <Chip
                  label="Featured"
                  color="primary"
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}

              {/* Organic Badge */}
              {product.farmingMethod === 'Organic' && (
                <Chip
                  label="Organic"
                  size="small"
                  color="success"
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 18,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
              
              {/* Category */}
              {product.category && (
                <Chip
                  label={typeof product.category === 'string' ? product.category : product.category.name}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 18,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}

              {/* Stock Warning */}
              {isLowStock && (
                <Chip
                  label={`Only ${product.stock} left`}
                  size="small"
                  color="warning"
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: 18,
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              )}
            </Box>

            {/* Short Description */}
            {(product.shortDescription || product.description) && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  fontSize: '0.875rem',
                  lineHeight: 1.3,
                  fontWeight: 400,
                  color: ink.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {product.shortDescription || product.description}
              </Typography>
            )}

            {/* Price and Actions Row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mt: 'auto'
            }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      color: brand.mustardInk
                    }}
                  >
                    {formatPrice(product.discountPrice || product.price)}
                  </Typography>
                  {product.discountPrice && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textDecoration: 'line-through',
                        color: ink.muted,
                        fontSize: '0.75rem'
                      }}
                    >
                      {formatPrice(product.price)}
                    </Typography>
                  )}
                </Box>
                
                {/* Unit info */}
                {product.unit && (
                  <Typography 
                    variant="caption" 
                    sx={{ fontSize: '0.7rem', color: ink.muted }}
                  >
                    per {product.unit}
                  </Typography>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(product);
                  }}
                  sx={{ 
                    bgcolor: surface.card,
                    border: `1.5px solid ${isFavorited ? status.error : line.default}`,
                    boxShadow: '0 2px 8px rgba(75,58,55,0.08)',
                    p: 0.75,
                    width: 36,
                    height: 36,
                    color: isFavorited ? status.error : ink.secondary,
                    '&:hover': {
                      bgcolor: isFavorited ? 'rgba(163,46,34,0.08)' : surface.sunken,
                      borderColor: isFavorited ? status.error : brand.brown,
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(75,58,55,0.16)'
                    }
                  }}
                >
                  {isFavorited ? <Favorite sx={{ fontSize: 18 }} /> : <FavoriteBorder sx={{ fontSize: 18 }} />}
                </IconButton>
                
                <IconButton
                  color="primary"
                  size="small"
                  disabled={isOutOfStock}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                  sx={{ 
                    bgcolor: brand.brown,
                    color: ink.onDark,
                    p: 0.5,
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    },
                    '&:disabled': {
                      bgcolor: surface.sunken,
                      color: ink.muted
                    }
                  }}
                >
                  <AddShoppingCart fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Seo
        title="Organic Produce from Our Birbhum Farm"
        description="Fruit, vegetables and pantry staples grown without chemicals on our permaculture farm in Birbhum, West Bengal, and delivered fresh after the harvest."
        path="/products"
      />

      {/* Mobile Header with Search and Actions */}
      {isActuallyMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Modern Mobile Hero with Stacked Images */}
          <Box sx={{ position: 'relative', height: 450, mb: 3, px: 1 }}>
            {/* Background image - largest */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '70%',
                height: '60%',
                borderRadius: '20px',
                overflow: 'hidden',
                backgroundImage: `url(${productBanner})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
                zIndex: 1,
                transition: 'transform 0.3s ease',
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            />
            
            {/* Middle image - medium */}
            <Box
              sx={{
                position: 'absolute',
                top: '25%',
                right: 0,
                width: '50%',
                height: '45%',
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundImage: `url(${productImage2})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 20px 35px rgba(0,0,0,0.15)',
                zIndex: 2,
                transition: 'transform 0.3s ease',
                '&:active': {
                  transform: 'scale(0.95) rotate(1deg)'
                }
              }}
            />
            
            {/* Front image - smallest */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: '20%',
                width: '40%',
                height: '35%',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundImage: `url(${productImage3})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 25px 45px rgba(0,0,0,0.2)',
                zIndex: 3,
                transition: 'transform 0.3s ease',
                '&:active': {
                  transform: 'scale(0.92) rotate(-2deg)'
                }
              }}
            />
            
            
            {/* Floating elements for mobile */}
            <Box
              sx={{
                position: 'absolute',
                top: '15%',
                left: '5%',
                width: 25,
                height: 25,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${brand.olive}, ${brand.oliveInk})`,
                opacity: 0.15,
                animation: 'float 5s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                  '50%': { transform: 'translateY(-15px) rotate(180deg)' }
                }
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '60%',
                right: '45%',
                width: 20,
                height: 20,
                borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                background: `linear-gradient(135deg, ${brand.clay}, ${brand.mustard})`,
                opacity: 0.12,
                animation: 'float 3s ease-in-out infinite reverse'
              }}
            />
          </Box>
          
          {/* Mobile Content Section */}
          <Box sx={{ px: 2, mb: 3 }}>
            {/* Mobile Title */}
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontSize: '2rem',
                fontWeight: 800,
                mb: 2,
                textAlign: 'center',
                color: ink.primary,
                letterSpacing: '-0.02em',
                background: `linear-gradient(135deg, ${ink.primary} 0%, ${brand.mustardInk} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Fresh Organic Products
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center',
                color: ink.secondary,
                mb: 3,
                fontSize: '1rem',
                lineHeight: 1.7
              }}
            >
              Farm-fresh produce delivered straight to your doorstep. Experience the taste of nature.
            </Typography>
            
            {/* Mobile Search Bar */}
            <TextField
              fullWidth
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                )
              }}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: '1rem',
                  bgcolor: 'background.paper',
                  boxShadow: '0 6px 20px rgba(75,58,55,0.08)',
                  border: `1px solid ${line.subtle}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(75,58,55,0.12)',
                    borderColor: 'primary.light'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 8px 25px rgba(75,58,55,0.15)',
                    borderColor: 'primary.main'
                  }
                },
                '& .MuiInputBase-input': {
                  py: 1.5
                }
              }}
            />
          </Box>
          
          {/* Mobile Search Bar with Actions */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              backgroundColor: surface.card,
              border: `1px solid ${line.subtle}`,
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: '0.9rem'
                  }
                }}
              />
              
              {/* Filter Button */}
              <IconButton
                onClick={() => setFiltersModalOpen(true)}
                color="primary"
                sx={{ 
                  border: 1, 
                  borderColor: 'primary.main',
                  position: 'relative'
                }}
              >
                <Badge badgeContent={getActiveFiltersCount()} color="error">
                  <FilterIcon />
                </Badge>
              </IconButton>
              
              {/* Refresh Button */}
              <IconButton
                onClick={() => loadProducts(true)}
                disabled={loading}
                color="primary"
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            
            {/* Active filters chips */}
            {getActiveFiltersCount() > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {filters.categories.map(category => (
                  <Chip
                    key={category}
                    label={category}
                    size="small"
                    onDelete={() => setFilters(prev => ({
                      ...prev,
                      categories: prev.categories.filter(c => c !== category)
                    }))}
                  />
                ))}
                {filters.farmingMethods.map(method => (
                  <Chip
                    key={method}
                    label={method}
                    size="small"
                    onDelete={() => setFilters(prev => ({
                      ...prev,
                      farmingMethods: prev.farmingMethods.filter(m => m !== method)
                    }))}
                  />
                ))}
                {(filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice) && (
                  <Chip
                    label={`₹${filters.priceRange[0]} - ₹${filters.priceRange[1]}`}
                    size="small"
                    onDelete={() => setFilters(prev => ({
                      ...prev,
                      priceRange: [0, maxPrice]
                    }))}
                  />
                )}
              </Box>
            )}
          </Paper>
          
          {/* Products Count */}
          <Typography variant="body2" sx={{ mb: 2, px: 1, color: ink.secondary, fontWeight: 500 }}>
            {loading ? 'Loading...' : `${filteredProducts.length} products found`}
          </Typography>
        </Box>
      ) : (
        /* Modern Desktop Hero with Stacked Images */
        <Box sx={{ mb: 6 }}>
          <Container maxWidth="xl">
            <Grid container spacing={4} alignItems="center" sx={{ minHeight: { md: 600 } }}>
              {/* Left Side - Stacked Images (60%) */}
              <Grid item xs={12} md={7}>
                <Box sx={{ position: 'relative', height: { xs: 400, md: 550 } }}>
                  {/* Background image - largest */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '65%',
                      height: '70%',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      backgroundImage: `url(${productBanner})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                      zIndex: 1,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)'
                      }
                    }}
                  />
                  
                  {/* Middle image - medium */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '20%',
                      right: 0,
                      width: '45%',
                      height: '50%',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      backgroundImage: `url(${productImage2})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                      zIndex: 2,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05) rotate(2deg)'
                      }
                    }}
                  />
                  
                  {/* Front image - smallest */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '25%',
                      width: '35%',
                      height: '35%',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      backgroundImage: `url(${productImage3})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 30px 60px rgba(0,0,0,0.2)',
                      zIndex: 3,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.08) rotate(-3deg)'
                      }
                    }}
                  />
                  
                  {/* Floating elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '10%',
                      right: '5%',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${brand.olive}, ${brand.oliveInk})`,
                      opacity: 0.1,
                      animation: 'float 6s ease-in-out infinite',
                      zIndex: 0,
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                        '50%': { transform: 'translateY(-20px) rotate(180deg)' }
                      }
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '20%',
                      right: '25%',
                      width: 40,
                      height: 40,
                      borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                      background: `linear-gradient(135deg, ${brand.clay}, ${brand.mustard})`,
                      opacity: 0.1,
                      animation: 'float 4s ease-in-out infinite reverse',
                      zIndex: 0
                    }}
                  />
                </Box>
              </Grid>
              
              {/* Right Side - Text and Search (40%) */}
              <Grid item xs={12} md={5}>
                <Box sx={{ pl: { md: 4 } }}>
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 800,
                      mb: 3,
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      color: ink.primary,
                      background: `linear-gradient(135deg, ${ink.primary} 0%, ${brand.mustardInk} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textAlign: { xs: 'center', md: 'left' }
                    }}
                  >
                    Fresh Organic Products
                  </Typography>
                  
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4,
                      fontSize: { xs: '1.2rem', md: '1.4rem' },
                      color: ink.secondary,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      textAlign: { xs: 'center', md: 'left' }
                    }}
                  >
                    Farm-fresh produce delivered straight to your doorstep. Experience the taste of nature with our organic collection.
                  </Typography>
                  
                  {/* Search Bar */}
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      placeholder="Search for fresh fruits, vegetables, and more..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          fontSize: '1.1rem',
                          bgcolor: 'background.paper',
                          boxShadow: '0 8px 32px rgba(75,58,55,0.08)',
                          border: `1px solid ${line.subtle}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 12px 40px rgba(75,58,55,0.12)',
                            borderColor: 'primary.light'
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 12px 40px rgba(75,58,55,0.15)',
                            borderColor: 'primary.main'
                          }
                        },
                        '& .MuiInputBase-input': {
                          py: 2
                        }
                      }}
                    />
                  </Box>
                  
                  
                  {/* Refresh Button */}
                  <Box sx={{ mt: 4, display: { xs: 'flex', md: 'block' }, justifyContent: 'center' }}>
                    <Tooltip title="Refresh products">
                      <IconButton 
                        onClick={() => loadProducts(true)} 
                        disabled={loading}
                        sx={{
                          bgcolor: brand.brown,
                          color: ink.onDark,
                          boxShadow: '0 8px 24px rgba(75,58,55,0.28)',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            boxShadow: '0 12px 32px rgba(75,58,55,0.36)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                        size="large"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* Content Area */}
      {isActuallyMobile ? (
        /* Mobile List View */
        <Box sx={{ px: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography>Loading products...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="error">{error}</Typography>
              <Button onClick={() => loadProducts(true)} sx={{ mt: 2 }}>
                Try Again
              </Button>
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 3,
                backgroundColor: surface.card,
                border: `1px solid ${line.subtle}`,
                borderRadius: 3,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 700, color: ink.primary }}
              >
                No products found
              </Typography>
              <Typography variant="body2" sx={{ color: ink.secondary }}>
                Try adjusting your search or filters
              </Typography>
            </Box>
          ) : (
            <Box>
              {filteredProducts.map(product => (
                <MobileProductCard key={product.id} product={product} />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop Grid View */
        <Grid container spacing={3}>
          {/* Filters Sidebar */}
          <Grid item xs={12} md={3}>
            <Box sx={{ 
              position: 'sticky',
              top: 20,
              maxHeight: 'calc(100vh - 40px)',
              overflow: 'auto'
            }}>
              <ProductFilter
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onClearFilters={handleClearFilters}
                maxPrice={maxPrice}
                categories={categoriesData}
                farmingMethods={farmingMethods}
              />
            </Box>
          </Grid>

          {/* Products Grid */}
          <Grid item xs={12} md={9}>
            <ProductGrid
              products={filteredProducts}
              loading={loading}
              error={error}
              onAddToCart={handleAddToCart}
              onToggleFavorite={handleToggleFavorite}
              onShare={handleShare}
              onProductClick={handleProductClick}
              onLoginRequired={handleLoginRequired}
              gridColumns={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }}
            />
          </Grid>
        </Grid>
      )}

      {/* Filters Modal for Mobile */}
      <Dialog
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        fullScreen={isActuallyMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: ink.primary }}>Filters</Typography>
          <IconButton onClick={() => setFiltersModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <ProductFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            maxPrice={maxPrice}
            categories={categoriesData}
            farmingMethods={farmingMethods}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setFiltersModalOpen(false)}
            variant="outlined"
            fullWidth
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProductsPage;